import { Location } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CurrencyExchangeModel } from 'src/app/models/archive/currency-exchage.model';
import { CurrencyExchangeService } from 'src/app/services/archive/currency-exchange.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-currency-exchange',
  templateUrl: './register-currency-exchange.component.html',
  styleUrls: ['./register-currency-exchange.component.scss'],
  standalone: false
})
export class RegisterCurrencyExchangeComponent implements OnInit {

  currencyExchangeContract: CurrencyExchangeModel = new CurrencyExchangeModel();
  currencyExchangeId: number = 0;
  disabledForm: boolean = false;
  formCurrencyExchange: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listCurrencies: any[] = [];
  nameWindow: string = 'Captura Tipos de Cambio';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = 'currency-exchange';

  constructor(private activatedRoute: ActivatedRoute,
    private confirmDialogService: ConfirmDialogService,
    private currencyExchangeService: CurrencyExchangeService,
    private dateConverterService: DateConverterService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
    this.validateForm(new CurrencyExchangeModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.currencyExchangeId = Object.keys(params).length > 0 ? params?.id : null;
    });
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  changeCurrency(currencyId: number) {
    this.currencyExchangeId = 0;
    this.currencyExchangeContract = new CurrencyExchangeModel();
    this.currencyExchangeContract.currencyId = currencyId;
    this.validateForm(this.currencyExchangeContract);
  }

  changeDateExchange() {
    const currencyId = this.formCurrencyExchange.get("currencyId")?.value;
    if (currencyId == 0 || currencyId == null) {
      this.toastHandlerService.showToast(
        'warning',
        `Debe seleccionar la moneda primero.`
      );
      return;
    }
    const dateExchange = this.formCurrencyExchange.get("dateExchange")?.value;
    if (dateExchange) {
      this.getCurrencyExchangeByCode(currencyId, dateExchange);
    } else {
      this.createNewRegister();
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para crear tipos de cambio. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.currencyExchangeId = 0;
    this.currencyExchangeContract = new CurrencyExchangeModel();
    this.validateForm(this.currencyExchangeContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para eliminar tipos de cambio. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.currencyExchangeId || this.currencyExchangeId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear un tipo de cambio.'
      );
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar el tipo de cambio?')) {
      try {
        await firstValueFrom(
          this.currencyExchangeService.deleteCurrencyExchange(this.currencyExchangeContract.currencyExchangeId)
        );
        this.createNewRegister();
        this.toastHandlerService.showToast(
          'success',
          'el tipo de cambio fue eliminado exitosamente.'
        );
      } catch (error) {
        console.error('Error al eliminar el tipo de cambio.', error);
      }
    }
  }

  async getListCurrencies() {
    try {
      const currencies = await firstValueFrom(this.currencyExchangeService.getAllCurrenciesForSelectAsync());
      this.listCurrencies = currencies;
    } catch (error) {
      console.error('Error al obtener la lista de monedas.', error);
    }
  }

  async getCurrencyExchangeByCode(currencyId: number, dateExchange: Date) {
    try {
      const response = await firstValueFrom(this.currencyExchangeService.getCurrencyExchangeByCode(currencyId,
        this.dateConverterService.convertDatePreserveDay(dateExchange)));
      this.currencyExchangeId = response.currencyExchangeId;
      this.currencyExchangeContract = response;
      this.validateForm(response);
      this.location.replaceState(`/${this.url}/register/${this.currencyExchangeId}`);
    } catch (error) {
      console.error('Error al obtener el tipo de cambio por código.', error);
    }
  }

  async getCurrencyExchangeById() {
    try {
      const response = await firstValueFrom(
        this.currencyExchangeService.getCurrencyExchangeById(this.currencyExchangeId)
      );
      this.currencyExchangeContract = response;
      this.validateForm(this.currencyExchangeContract);
      this.location.replaceState(`/${this.url}/register/${this.currencyExchangeId}`);
    } catch (error) {
      console.error('Error al obtener el tipo de cambio por id.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getListCurrencies(),
      this.getPermissionProfileByUrl(),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async handleInitialState() {
    if (this.currencyExchangeId !== null && this.currencyExchangeId !== 0) {
      await this.getCurrencyExchangeById();
    } else {
      this.createNewRegister();
    }
  }

  async send() {
    if (this.formCurrencyExchange.valid) {
      const modelCurrencyExchange = this.formCurrencyExchange.getRawValue() as CurrencyExchangeModel;
      if (modelCurrencyExchange.currencyId == 0 || modelCurrencyExchange.currencyId == null) {
        this.toastHandlerService.showToast(
          'warning',
          'Debe seleccionar primero la moneda.'
        );
        return;
      }
      if (this.currencyExchangeId == 0) {
        try {
          const response = await firstValueFrom(
            this.currencyExchangeService.createCurrencyExchange(modelCurrencyExchange)
          );
          this.currencyExchangeId = response.currencyExchangeId;
          this.currencyExchangeContract = response;
          this.validateForm(this.currencyExchangeContract);
          this.location.replaceState(`/${this.url}/register/${this.currencyExchangeId}`);
          this.toastHandlerService.showToast(
            'success',
            `El tipo de cambio fue creado exitosamente.`
          );
        } catch (error) {
          console.error('Error al crear el tipo de cambio.', error);
        }
      } else {
        this.currencyExchangeContract.valueExchange = modelCurrencyExchange.valueExchange;
        try {
          const response = await firstValueFrom(
            this.currencyExchangeService.updateCurrencyExchange(this.currencyExchangeContract)
          );
          this.currencyExchangeContract = response;
          this.validateForm(this.currencyExchangeContract);
          this.toastHandlerService.showToast(
            'success',
            `El tipo de cambio fue modificado exitosamente.`
          );
        } catch (error) {
          console.error('Error al actualizar el tipo de cambio.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      currencyId: {
        required: 'Debe seleccionar una Moneda.',
        min: 'Debe seleccionar una Moneda válida.'
      },
      dateExchange: {
        required: 'Debe ingresar la Fecha de cambio.'
      },
      valueExchange: {
        required: 'Debe ingresar el Valor de cambio.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formCurrencyExchange.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(currencyExchange: CurrencyExchangeModel) {
    this.formCurrencyExchange = this.formBuilder.group({
      currencyId: [currencyExchange.currencyId, [Validators.required, Validators.min(1)]],
      dateExchange: [currencyExchange.dateExchange ? this.dateConverterService.convertDatePreserveDay(currencyExchange.dateExchange) : null, [Validators.required]],
      valueExchange: [currencyExchange.valueExchange, [Validators.required]]
    });
  }

  validateFormControls() {
    Object.keys(this.formCurrencyExchange.controls).forEach((key) => {
      const control = this.formCurrencyExchange.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
